/**
 * SurveyForm
 *
 * Renders a survey and submits answers to the survey response API.
 *
 * Primary responsibilities:
 * - Render supported survey question types
 * - Validate required answers before submission
 * - Notify parent surfaces when a response is saved
 *
 * Dependencies:
 * - Survey type contracts
 * - Shared UI form controls
 */

'use client'

import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { cn } from '@/lib/utils'
import type {
  SurveyAnswerValue,
  SurveyAnswers,
  SurveyQuestion,
  SurveyResponseSource,
  SurveyRow
} from '@/lib/surveys/types'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'

interface SurveyFormProps {
  survey: SurveyRow
  source: SurveyResponseSource
  className?: string
  onSubmitted?: () => void
}

// --------------

function hasAnswer(value: SurveyAnswerValue | undefined) {
  if (Array.isArray(value)) {
    return value.length > 0
  }

  return value !== undefined && value !== null && String(value).trim() !== ''
}

// --------------

function getInitialAnswers(questions: SurveyQuestion[]): SurveyAnswers {
  return questions.reduce<SurveyAnswers>((answers, question) => {
    if (question.type === 'multi_choice') {
      answers[question.id] = []
    }

    return answers
  }, {})
}

// --------------

function getValidationError(questions: SurveyQuestion[], answers: SurveyAnswers) {
  const missingQuestion = questions.find((question) => {
    return question.required && !hasAnswer(answers[question.id])
  })

  return missingQuestion
    ? {
        questionId: missingQuestion.id,
        message: `${missingQuestion.label} is required.`
      }
    : null
}

// --------------

function getRatingValues(question: SurveyQuestion) {
  const min = question.scale?.min ?? 1
  const max = question.scale?.max ?? 5
  const count = Math.max(1, max - min + 1)

  return Array.from({ length: count }, (_item, index) => min + index)
}

// --------------

function getStringAnswer(answers: SurveyAnswers, questionId: string) {
  const answer = answers[questionId]
  return typeof answer === 'string' ? answer : ''
}

// --------------

export function SurveyForm({ survey, source, className, onSubmitted }: SurveyFormProps) {
  const [answers, setAnswers] = useState<SurveyAnswers>(() => getInitialAnswers(survey.questions))
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const validationError = useMemo(
    () => getValidationError(survey.questions, answers),
    [answers, survey.questions]
  )

  const setAnswer = (questionId: string, value: SurveyAnswerValue) => {
    setAnswers((current) => ({
      ...current,
      [questionId]: value
    }))
  }

  const toggleMultiChoice = (questionId: string, option: string, checked: boolean) => {
    setAnswers((current) => {
      const currentValue = current[questionId]
      const options = Array.isArray(currentValue) ? currentValue : []
      const nextOptions = checked ? [...options, option] : options.filter((item) => item !== option)

      return {
        ...current,
        [questionId]: nextOptions
      }
    })
  }

  const submit = async () => {
    if (validationError) {
      setErrorMessage(validationError.message)
      toast.error(validationError.message)
      return
    }

    setErrorMessage(null)
    setSubmitting(true)

    try {
      const response = await fetch(`/api/surveys/${survey.id}/responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source,
          answers
        })
      })

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { error?: string }
        throw new Error(payload.error || 'Failed to submit survey')
      }

      setSubmitted(true)
      onSubmitted?.()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to submit survey')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div
        className={cn('border-border bg-muted/30 rounded-lg border p-4', className)}
        role="status"
        aria-live="polite"
      >
        <div className="text-sm font-medium">Thanks for the feedback.</div>
        <p className="text-muted-foreground mt-1 text-sm">
          Your response was saved and will help shape what gets improved next.
        </p>
      </div>
    )
  }

  if (survey.questions.length === 0) {
    return (
      <div className={cn('border-border bg-muted/30 rounded-lg border p-4', className)}>
        <div className="text-sm font-medium">This survey has no questions yet.</div>
        <p className="text-muted-foreground mt-1 text-sm">
          Check back later when the survey is ready for responses.
        </p>
      </div>
    )
  }

  return (
    <div className={cn('space-y-5', className)}>
      {errorMessage ? (
        <div className="border-destructive/30 bg-destructive/5 text-destructive rounded-lg border p-3 text-sm" role="alert">
          {errorMessage}
        </div>
      ) : null}

      {survey.questions.map((question, index) => {
        const labelId = `survey-question-${question.id}-label`
        const hasError = validationError?.questionId === question.id

        return (
          <div
            key={question.id}
            className={cn(
              'space-y-3 rounded-lg border p-4',
              hasError && 'border-destructive/50 bg-destructive/5'
            )}
            role="group"
            aria-labelledby={labelId}
          >
            <div className="space-y-1">
              <Label id={labelId} className="block text-sm font-medium leading-5">
                <span className="text-muted-foreground mr-1.5 text-xs">Q{index + 1}</span>
                {question.label}
                {question.required ? (
                  <span className="text-destructive ml-1" aria-hidden="true">
                    *
                  </span>
                ) : null}
              </Label>
              <div className="text-muted-foreground text-xs">
                {question.required ? 'Required' : 'Optional'}
              </div>
            </div>

            {question.type === 'rating' ? (
              <div className="space-y-2">
                <div className="grid grid-cols-[repeat(auto-fit,minmax(42px,1fr))] gap-2">
                  {getRatingValues(question).map((value) => (
                    <Button
                      key={value}
                      type="button"
                      variant={answers[question.id] === value ? 'default' : 'outline'}
                      className="h-10 min-w-0 px-2"
                      aria-pressed={answers[question.id] === value}
                      aria-label={`Rate ${value}`}
                      disabled={submitting}
                      onClick={() => setAnswer(question.id, value)}
                    >
                      {value}
                    </Button>
                  ))}
                </div>
                {(question.scale?.minLabel || question.scale?.maxLabel) && (
                  <div className="text-muted-foreground flex justify-between gap-3 text-xs">
                    <span>{question.scale?.minLabel}</span>
                    <span>{question.scale?.maxLabel}</span>
                  </div>
                )}
              </div>
            ) : null}

            {question.type === 'short_text' ? (
              <Input
                value={getStringAnswer(answers, question.id)}
                aria-labelledby={labelId}
                aria-invalid={hasError}
                disabled={submitting}
                maxLength={240}
                onChange={(event) => setAnswer(question.id, event.target.value)}
              />
            ) : null}

            {question.type === 'long_text' ? (
              <Textarea
                value={getStringAnswer(answers, question.id)}
                className="min-h-[112px] resize-none"
                aria-labelledby={labelId}
                aria-invalid={hasError}
                disabled={submitting}
                maxLength={2000}
                onChange={(event) => setAnswer(question.id, event.target.value)}
              />
            ) : null}

            {question.type === 'single_choice' ? (
              <RadioGroup
                value={getStringAnswer(answers, question.id)}
                aria-labelledby={labelId}
                onValueChange={(value) => setAnswer(question.id, value)}
              >
                {(question.options ?? []).map((option, optionIndex) => {
                  const optionId = `${question.id}-option-${optionIndex}`

                  return (
                    <Label key={option} htmlFor={optionId} className="flex items-center gap-2 text-sm">
                      <RadioGroupItem id={optionId} value={option} disabled={submitting} />
                      {option}
                    </Label>
                  )
                })}
              </RadioGroup>
            ) : null}

            {question.type === 'multi_choice' ? (
              <div className="space-y-2">
                {(question.options ?? []).map((option, optionIndex) => {
                  const selected = answers[question.id]
                  const checked = Array.isArray(selected) && selected.includes(option)
                  const optionId = `${question.id}-option-${optionIndex}`

                  return (
                    <Label key={option} htmlFor={optionId} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        id={optionId}
                        checked={checked}
                        disabled={submitting}
                        onCheckedChange={(nextChecked) =>
                          toggleMultiChoice(question.id, option, nextChecked === true)
                        }
                      />
                      {option}
                    </Label>
                  )
                })}
              </div>
            ) : null}
          </div>
        )
      })}

      <Button type="button" className="w-full" disabled={submitting} onClick={submit}>
        {submitting ? 'Sending...' : 'Send feedback'}
      </Button>
    </div>
  )
}
